import type { ColorDictionary, CurrentInfo, GeometryProperties, Region, RegionGeometry } from '@protector/entities/region/models';
import type { ScoutingScorePerField } from '@protector/entities/scouting-score/models';
import { LissThresholds, Threshold } from '@protector/utils/constants';
import type { LatLngTuple, UUID } from '@protector/utils/types';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import type { Coord, MultiPolygon } from '@turf/helpers';
import { API_REMOTE_SENSING_URL } from 'config/constants';
import { COLOR_OPTIONS } from 'config/style';
import { getCurrentLanguage } from 'core/utils/functions';
import { getNemadigitalColor, getNematodeSeverity, hasNemaMapDisclaimer } from 'core/utils/map/choropleth';
import type { Feature, FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import moment from 'moment';
import { isAValidRegionGeometry } from 'pages/disease-risk/map/map.utils';
import { scoutingScoreColors } from 'pages/timeline/area-day-info/components/application-details.functions';
import type { TrackingSegment } from 'pages/timeline/timeline.models';
import type { NemadigitalCropCycleReport } from 'syngenta-digital-react-cropwise-nemadigital/dist/module/map-overlay/map-overlay.types';

export const DEFAULT_UUID = '00000000-0000-0000-0000-000000000000';

const getCoordinatesInPolygon = (polygon: RegionGeometry, coordinates: Coord[]): Coord[][] => {
  const initialValue: Record<number, Coord[]> = { 0: [] };
  const coordsReduced = coordinates.reduce((acc: Record<number, Coord[]>, coordinate) => {
    const lastIndex = Object.keys(acc).length - 1;

    if (isAValidRegionGeometry(polygon) && !booleanPointInPolygon(coordinate, polygon as unknown as MultiPolygon)) {
      if (acc[lastIndex].length) {
        return { ...acc, [lastIndex + 1]: [] };
      }
      return { ...acc };
    }
    let lastSection: Coord[] = [];
    if (acc[lastIndex]) {
      lastSection = acc[lastIndex];
    }

    lastSection.push(coordinate);
    return { ...acc, [lastIndex]: lastSection };
  }, initialValue);

  return Object.values(coordsReduced);
};

/**
 * Return sections for all coordinates inside polygon
 * @param polygon
 * @param coordinates
 */
export function sliceTrackingRoute(
  coordinates: Coord[],
  segments: TrackingSegment[],
  polygon?: RegionGeometry,
  enableTrackingBySegments?: boolean
): Coord[][] {
  if (!polygon) {
    return [];
  }

  if (!enableTrackingBySegments) {
    return getCoordinatesInPolygon(polygon, coordinates);
  }

  return segments.flatMap(segment => {
    const coords = coordinates.slice(segment.start_index, segment.end_index + 1);
    return getCoordinatesInPolygon(polygon, coords);
  });
}

export const getAreaThreshold = (area: Region): Threshold => {
  if (!area?.current_info?.last_monitoring) return Threshold.NONE;

  const severityLevel = area.current_info.severity_level;
  if (severityLevel === null) return Threshold.NONE;

  if (severityLevel < (LissThresholds.MIN_CONTROL as number)) {
    return Threshold.ACCEPTANCE;
  }

  if (severityLevel < (LissThresholds.MIN_DAMAGE as number)) {
    return Threshold.CONTROL;
  }

  return Threshold.DAMAGE;
};

const options = [Threshold.ENDED, Threshold.SPRAY];

export const setAreaHasLastSprayAfterMonitoring = (region: Region, seasonEnded: boolean): boolean => {
  if (seasonEnded) return false;
  const lastSpray = region?.current_info?.last_spray?.end_date;
  if (!lastSpray) return false;
  const lastMonitoring = region?.current_info?.last_monitoring?.date;

  const severityLevel = getAreaThreshold(region);
  const validatedField = !severityLevel || (severityLevel && options.includes(severityLevel));
  if (validatedField || !lastMonitoring) return true;

  const parsedLastSpray = moment(lastSpray).format('YYYY-MM-DD HH:mm');
  const parsedLastMonitoring = moment(lastMonitoring).format('YYYY-MM-DD HH:mm');

  return moment(parsedLastSpray).isAfter(parsedLastMonitoring);
};

export function addScoutingScoreToRegionProperties(
  region: Region,
  latestScoutingScore: ScoutingScorePerField = {}
): FeatureCollection<Geometry, GeometryProperties> {
  const features = (region.geometry as FeatureCollection<Geometry, GeoJsonProperties>)?.features.map(regionGeometry => {
    const areaScoutingScore = latestScoutingScore[regionGeometry.properties?.id as string];
    return {
      ...regionGeometry,
      properties: {
        ...regionGeometry.properties,
        scoutingScore: areaScoutingScore?.score,
        scoutingScoreColor: scoutingScoreColors(areaScoutingScore?.score).fill,
        scouters: areaScoutingScore?.scoutersScores.map(s => s.scouter_id)
      }
    };
  });
  return {
    type: 'FeatureCollection',
    features
  };
}

export function addNematodeDamageToRegionProperties(
  region: Region,
  nemadigitalReport: NemadigitalCropCycleReport,
  isUSAUsers: boolean,
  hiddenFieldsIds: UUID[],
  lastSoySeasonByField?: Record<string, string | undefined>
): FeatureCollection<Geometry, GeometryProperties> {
  const features = (region.geometry as FeatureCollection<Geometry, GeoJsonProperties>)?.features.map(regionGeometry => {
    const { damagePerField } = nemadigitalReport;
    const fieldId = regionGeometry.properties?.id as string;
    const fieldNematodeDamage = damagePerField[fieldId];

    const damageValue = Number(fieldNematodeDamage);
    const formattedDamage = Number.isNaN(damageValue)
      ? undefined
      : `${damageValue.toLocaleString(getCurrentLanguage(), {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2
        })}%`;

    const severityColor = Number.isNaN(damageValue) ? undefined : getNemadigitalColor(damageValue, isUSAUsers);

    return {
      ...regionGeometry,
      properties: {
        ...regionGeometry.properties,
        nematodeDamage: formattedDamage,
        nematodeColor: severityColor,
        showDisclaimerIcon: Number.isNaN(damageValue) ? false : hasNemaMapDisclaimer(damageValue, isUSAUsers),
        nematodeSeverity: Number.isNaN(damageValue) ? undefined : getNematodeSeverity(severityColor, isUSAUsers),
        nematodeFieldHidden: hiddenFieldsIds?.includes(fieldId as UUID),
        lastSoybeanSeason: lastSoySeasonByField?.[fieldId]
      }
    };
  }) as Feature<Geometry, GeometryProperties>[];

  return {
    type: 'FeatureCollection',
    features
  };
}

export function isNumberInRange(dae: number, highlightValue: string | undefined): boolean {
  const value = dae || 0;

  if (highlightValue?.includes('>')) {
    const formatValue = Number(highlightValue.replace('>', ''));
    return dae >= formatValue;
  }
  const min = highlightValue?.split('-')[0];
  const max = highlightValue?.split('-')[1];

  return value >= Number(min) && value < Number(max);
}

export function updateRegionsHighlightStatus(
  region: Region,
  highlightFilter: { field: string; value: string | undefined }
): FeatureCollection<Geometry, GeometryProperties> {
  const features = (region.geometry as FeatureCollection<Geometry, GeoJsonProperties>)?.features.map(regionGeometry => {
    const dae: number | string = regionGeometry.properties?.[highlightFilter.field] as string;
    let highlight = false;

    const daeBetweenRange = isNumberInRange(dae as unknown as number, highlightFilter?.value);

    if (highlightFilter.value) {
      highlight = dae === highlightFilter.value || daeBetweenRange;
    }

    return {
      ...regionGeometry,
      properties: {
        ...regionGeometry.properties,
        highlight
      }
    };
  });
  return {
    type: 'FeatureCollection',
    features
  };
}

export function updateSeedAndPhenologyColorsToRegionProperties(
  region: Region,
  mapLayersColorDictionary: ColorDictionary
): FeatureCollection<Geometry, GeometryProperties> {
  const features = (region.geometry as FeatureCollection<Geometry, GeoJsonProperties>)?.features.map(regionGeometry => {
    const seed = (regionGeometry.properties?.current_info as CurrentInfo)?.seeds?.[0];
    const phenologicalStage = (regionGeometry.properties?.current_info as CurrentInfo)?.phenological_stage ?? '';
    const cropEnded = (regionGeometry.properties?.current_info as CurrentInfo)?.crop_ended;
    const seedColor = cropEnded ? COLOR_OPTIONS.GRAY_4 : mapLayersColorDictionary?.seedsColors?.[seed ?? '']?.color;
    const phenologicalStageColor = cropEnded
      ? COLOR_OPTIONS.GRAY_4
      : mapLayersColorDictionary?.phenologicalStageColors?.[phenologicalStage]?.color;
    return {
      ...regionGeometry,
      properties: {
        ...regionGeometry.properties,
        seedColor,
        phenologicalStageColor
      }
    };
  });
  return {
    type: 'FeatureCollection',
    features
  };
}

interface IFarmshotsLayer {
  id: string;
  tiles: string[];
  coordinates?: LatLngTuple;
}

interface IRemoteSensingLayersFactory {
  imageryLayers: IFarmshotsLayer[];
  exprs?: string;
}

interface RemoteSensingLayersFactoryParams {
  checkedAssets: string[];
  algorithm: string;
}

export const replaceAlgoritm: Record<string, string> = {
  NDVIR: 'ndvi',
  SAVI: 'savi',
  RGB: 'rgb'
};

export function RemoteSensingLayersFactory({ checkedAssets, algorithm }: RemoteSensingLayersFactoryParams): IRemoteSensingLayersFactory {
  const algorithmSelected = replaceAlgoritm[algorithm];
  const color = algorithmSelected !== 'rgb' ? 'falsecolor' : 'truecolor';
  const imageryLayers = checkedAssets?.map(assetId => {
    const tiles = [`${API_REMOTE_SENSING_URL}/images/${assetId}/${algorithmSelected}/${color}/tiles/{z}/{x}/{y}/png`];

    return {
      id: assetId,
      tiles
    };
  });

  return {
    imageryLayers
  };
}
